import {IconButton, Menu, MenuItem, Typography} from "@mui/material"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import LoopIcon from "@mui/icons-material/Loop"
import styled, {keyframes} from "styled-components"
import React, {useState} from "react"

import {api, OutstandingAccount} from "@indebted/api"
import {useAgent} from "@indebted/hooks/Agent"
import {useNotification} from "@indebted/components/Notification"
import {ConfirmationDialog} from "@indebted/components/ConfirmationDialog"

function AccountMenu({account, reload}: {account: OutstandingAccount; reload: (delayMs?: number) => void}) {
	const [agentEmail] = useAgent()
	const {notification} = useNotification()
	const [anchorEl, setAnchorEl] = useState<HTMLElement | undefined>()
	const [MenuIcon, setMenuIcon] = useState<React.ReactElement>(<MoreVertIcon />)
	const [confirmationDialogOpen, setConfirmationDialogOpen] = useState<boolean>(false)
	const [confirmationDialogOnOKFunc, setConfirmationDialogOnOKFunc] = useState<(() => void) | undefined>(undefined)
	const [confirmationDialogDescription, setConfirmationDialogDescription] = useState<string>("")

	const handleClick = (e: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(e.currentTarget)
	}

	const handleMenuClose = () => {
		setAnchorEl(undefined)
	}

	const handleMenuItemClick = (disputeReason: {ID: string; Reason: string}) => {
		setMenuIcon(<LoadingIcon />)

		api.account
			.dispute(account.ID, {
				Reason: disputeReason.ID,
				AgentEmail: agentEmail,
			})
			.then(() => {
				setMenuIcon(<MoreVertIcon />)
				notification.success(
					`Account disputed successfully. Please wait up to 48 hours for propagation in all systems.`,
				)
				setTimeout(reload, 500)
			})
			.catch((error) => {
				setMenuIcon(<MoreVertIcon />)
				notification.error(error.Message)
			})

		handleMenuClose()
	}

	const handleRecallClick = () => {
		setMenuIcon(<LoadingIcon />)

		api.account
			.recall(account.ID, {
				AgentEmail: agentEmail,
			})
			.then(() => {
				setMenuIcon(<MoreVertIcon />)
				notification.success(
					`Account recalled successfully. Please wait up to 48 hours for propagation in all systems.`,
				)
				reload(1000)
			})
			.catch((error) => {
				setMenuIcon(<MoreVertIcon />)
				notification.error(error.Message)
			})

		handleMenuClose()
	}

	const confirmDialog = (description: string, onOK: () => void) => {
		handleMenuClose()
		setConfirmationDialogDescription(description)
		setConfirmationDialogOnOKFunc(() => onOK)
		setConfirmationDialogOpen(true)
	}

	return (
		<>
			<ConfirmationDialog
				description={confirmationDialogDescription}
				isOpen={confirmationDialogOpen}
				onCancel={() => {
					setConfirmationDialogOpen(false)
				}}
				onOK={() => {
					confirmationDialogOnOKFunc?.()
					setConfirmationDialogOpen(false)
				}}
			/>

			<IconButton size="small" onClick={handleClick}>
				{MenuIcon}
			</IconButton>
			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				anchorOrigin={{vertical: "bottom", horizontal: "right"}}
				transformOrigin={{vertical: "top", horizontal: "right"}}
				onClose={handleMenuClose}
			>
				<Divider color="textSecondary" variant="caption">
					Dispute
				</Divider>
				{account.DisputeReasons.map((disputeReason) => (
					<MenuItem
						key={disputeReason.ID}
						onClick={() =>
							confirmDialog(`Dispute ${disputeReason.Reason} selected. Do you confirm?`, () =>
								handleMenuItemClick(disputeReason),
							)
						}
					>
						{disputeReason.Reason}
					</MenuItem>
				))}
				<Divider color="textSecondary" variant="caption">
					Recall
				</Divider>
				<MenuItem onClick={() => confirmDialog("Recall selected. Do you confirm?", () => handleRecallClick())}>
					Recall account
				</MenuItem>
			</Menu>
		</>
	)
}

const loadingIconAnimations = keyframes`
	from { transform:rotate(0deg); }
    to { transform:rotate(-360deg);}
`

const LoadingIcon = styled(LoopIcon)`
	animation: ${loadingIconAnimations} 4s infinite linear;
`

const Divider = styled(Typography)`
	padding: 0 6px;
`

export {AccountMenu}
